import type { AuditActionType, AuditEventType, AuditSeverityType } from '~/types/Audit'
import type { ChipColorType } from '~/types/Chip'

/**
 * Cómo se nombra y se pinta cada valor cerrado de la auditoría.
 *
 * Vive aquí y no en cada pantalla porque lo usan a la vez la tabla, los chips
 * de filtro, el desplegable de filtros y el modal de detalle: un color distinto
 * en cada sitio haría ilegible la lista.
 */

interface LabelStyleType {
  label: string
  color: ChipColorType
  icon: string
}

const ACTIONS: Record<AuditActionType, LabelStyleType> = {
  create: { label: 'Creación', color: 'success', icon: 'material-symbols-light:add-circle-outline' },
  update: { label: 'Actualización', color: 'warning', icon: 'material-symbols-light:edit-outline' },
  delete: { label: 'Eliminación', color: 'danger', icon: 'material-symbols-light:delete-outline' },
  status_change: { label: 'Cambio de estado', color: 'info', icon: 'material-symbols-light:swap-horiz' },
  auth: { label: 'Acceso', color: 'primary', icon: 'material-symbols-light:key-outline' },
  error: { label: 'Error', color: 'danger', icon: 'material-symbols-light:error-outline' },
  system: { label: 'Sistema', color: 'default', icon: 'material-symbols-light:settings-outline' },
  job: { label: 'Proceso', color: 'default', icon: 'material-symbols-light:schedule-outline' }
}

const SEVERITIES: Record<AuditSeverityType, LabelStyleType> = {
  debug: { label: 'Depuración', color: 'default', icon: 'material-symbols-light:bug-report-outline' },
  info: { label: 'Información', color: 'info', icon: 'material-symbols-light:info-outline' },
  warn: { label: 'Aviso', color: 'warning', icon: 'material-symbols-light:warning-outline' },
  error: { label: 'Error', color: 'danger', icon: 'material-symbols-light:error-outline' },
  critical: { label: 'Crítico', color: 'danger', icon: 'material-symbols-light:e911-emergency-outline' }
}

const UNKNOWN: LabelStyleType = { label: '—', color: 'default', icon: 'material-symbols-light:help-outline' }

/** Estilo de una acción. Un valor desconocido no rompe la tabla: cae a neutro. */
export const actionStyle = (action: string | null | undefined): LabelStyleType => ACTIONS[action as AuditActionType] ?? { ...UNKNOWN, label: action || '—' }

export const severityStyle = (severity: string | null | undefined): LabelStyleType => SEVERITIES[severity as AuditSeverityType] ?? { ...UNKNOWN, label: severity || '—' }

/** Opciones de los desplegables de filtro, en el orden en que se entienden. */
export const ACTION_OPTIONS = (Object.keys(ACTIONS) as AuditActionType[]).map(value => ({ value, title: ACTIONS[value].label }))

export const SEVERITY_OPTIONS = (Object.keys(SEVERITIES) as AuditSeverityType[]).map(value => ({ value, title: SEVERITIES[value].label }))

/** Nombre legible de quién originó la operación. */
const ACTORS: Record<string, string> = {
  user: 'Usuario',
  admin: 'Administrador',
  api: 'API',
  system: 'Sistema',
  cron: 'Tarea programada',
  worker: 'Proceso',
  integration: 'Integración'
}

export const actorLabel = (actor: string | null | undefined): string => ACTORS[actor ?? ''] ?? actor ?? '—'

/**
 * `before`/`after`/`changed`/`data`/`metadata` viajan como texto JSON. Nunca se
 * confía en el contenido: una cadena rota devuelve objeto vacío en vez de
 * tumbar la pantalla.
 */
export const parseJson = (value: string | null | undefined): Record<string, unknown> => {
  if (!value) return {}
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** ¿El objeto trae algo que merezca pintarse? */
export const hasContent = (value: Record<string, unknown>): boolean => Object.keys(value).length > 0

/** Un campo modificado, ya normalizado para la tabla de cambios del detalle. */
export interface ChangedFieldType {
  field: string
  from: unknown
  to: unknown
}

/**
 * Lista de campos modificados. El backend guarda `{ campo: { from, to } }`,
 * pero si un evento no lo trae (creación, borrado) se deduce comparando
 * `before` y `after` para que el detalle nunca salga vacío.
 */
export const changedFields = (event: AuditEventType): ChangedFieldType[] => {
  const changed = parseJson(event.changed)
  if (hasContent(changed)) {
    return (
      Object.entries(changed)
        .map(([field, value]) => {
          const pair = (value ?? {}) as { from?: unknown; to?: unknown }
          return { field, from: pair.from, to: pair.to }
        })
        // `null` frente a `undefined` entra en `changed` pero se lee igual en las
        // dos columnas ("— → —"): son ruido en una lista de cambios.
        .filter(cambio => formatValue(cambio.from) !== formatValue(cambio.to))
    )
  }

  const before = parseJson(event.before)
  const after = parseJson(event.after)
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  return keys.filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key])).map(key => ({ field: key, from: before[key], to: after[key] }))
}

/** Valor de un campo listo para leerse en una celda. */
export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Sí' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  const text = String(value)
  return text.trim() === '' ? '—' : text
}

/** JSON con sangría para los bloques del detalle. */
export const prettyJson = (value: Record<string, unknown>): string => JSON.stringify(value, null, 2)
